import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import {
  IconEye,
  IconBook2,
  IconCheck,
  IconTrash,
  IconXboxX,
  IconSchool,
  IconSearch,
  IconMoodKid,
  IconLockOpen,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import {
  SchoolStatusConfig,
  SubscriptionStatusConfig,
  SUBSCRIPTION_STATUS_OPTIONS,
} from '@/constants/constant';
import { ESchoolStatus, ERestoreEntityType, ESubscriptionStatus } from '@/enum';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  softdeleteRecord,
  blockUnblockRecordAPI,
} from '@/service/commonService';
import { getSchoolList, getSchoolSummary } from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import { ISchoolData, IStatsDataItem, ISelectedRowValue } from '@/types';
import {
  pickBy,
  getAssetUrl,
  customPredicate,
  getStartOfMonthUnix,
} from '@/utils';

import AddSchoolModal from './modals/AddSchoolModal';
import { SchoolDetailsModal } from './modals/SchoolDetailsModal';

const SUMMARY_CONFIG = [
  {
    key: 'totalSchool',
    icon: <IconSchool color="#717784" />,
    label: 'school.summary-cards.total-school',
  },
  {
    key: 'activeSchools',
    icon: <IconCheck color="#717784" />,
    label: 'school.summary-cards.active-school',
  },
  {
    key: 'totalTeacher',
    icon: <IconBook2 color="#717784" />,
    label: 'school.summary-cards.total-teacher',
  },
  {
    key: 'totalStudents',
    icon: <IconMoodKid color="#717784" />,
    label: 'school.summary-cards.total-student',
  },
];

const School = () => {
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();
  const [search, setSearch] = useState('');
  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);
  const [isRemoveSchoolModalOpen, setIsRemoveSchoolModalOpen] = useState(false);
  const [isAddSchoolModalOpen, setIsAddSchoolModalOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<
    ESubscriptionStatus | ''
  >('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  const [schools, setSchools] = useState<ISchoolData[]>([]);
  const [statsData, setStatsData] = useState<IStatsDataItem[]>(
    SUMMARY_CONFIG.map((item) => ({
      ...item,
      value: '0',
      trend: '0%',
      trendLabel: 'common.this-month',
      trendDirection: 'STABLE',
    }))
  );
  const {
    loadingTable,
    loadingSummary,
    setLoadingTable,
    setLoadingSummary,
    loadingModal,
    setLoadingModal,
  } = useCommonStore();
  const [selectedSchool, setSelectedSchool] =
    useState<ISelectedRowValue | null>(null);
  const pageTitle = formatText('school.title');
  const userManagement = formatText('user-management.title');

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, subscriptionStatus]);

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: userManagement }, { label: pageTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, userManagement]);

  const fetchSchools = async () => {
    setLoadingTable(true);
    const params = pickBy(
      {
        search: debouncedSearch || undefined,
        subscription_status: subscriptionStatus,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        order: sortField ? { [sortField]: sortDirection } : undefined,
      },
      customPredicate
    );
    const response = await getSchoolList(params);
    if (response?.status) {
      setSchools(response?.data || []);
      setTotal(response?.total || 0);
    }
    setLoadingTable(false);
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);

    const monthTimestamp = getStartOfMonthUnix();

    const response = await getSchoolSummary({ month: monthTimestamp });
    if (response?.status) {
      const apiData = response?.data;
      const mappedSummary = SUMMARY_CONFIG?.map((item) => {
        const itemData = apiData[item?.key as keyof typeof apiData];
        return {
          ...item,
          value: itemData?.total?.toLocaleString() || '0',
          trend: `${itemData?.growth || 0}%`,
          trendLabel: 'common.this-month',
          trendDirection: itemData?.trend || 'STABLE',
        };
      });
      setStatsData(mappedSummary);
    }
    setLoadingSummary(false);
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    subscriptionStatus,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
  ]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'school.columns.name',
      key: 'name',
      width: '240px',
      sorter: true,
      handleSort: () => handleSort('name'),
      render: (row: ISchoolData) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row.id,
                name: row.name,
                image: getAssetUrl(row?.asset),
              },
            ]}
            size={40}
            avatarClassName="w-9 h-9 text-sm"
            isSquare
          />
          <span className="text-sm font-medium text-neutral-primary">
            {row?.name || '-'}
          </span>
        </div>
      ),
    },
    {
      title: 'school.columns.email',
      key: 'email',
      width: '260px',
      sorter: true,
      handleSort: () => handleSort('email'),
      render: (row: ISchoolData) => (
        <span className="text-sm text-neutral-primary">
          {row?.email || '-'}
        </span>
      ),
    },
    {
      title: 'school.columns.teachers',
      key: 'total_teachers',
      width: '140px',
      sorter: true,
      handleSort: () => handleSort('total_teachers'),
      render: (row: { total_teachers: string }) => (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-semibold bg-pink-light-50 text-pink-secondary'
          )}
        >
          {row?.total_teachers ?? 0}
        </span>
      ),
    },
    {
      title: 'school.columns.students',
      key: 'total_students',
      width: '100px',
      sorter: true,
      handleSort: () => handleSort('total_students'),
      render: (row: { total_students: string }) => (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-semibold bg-indigo-light-50 text-indigo-secondary'
          )}
        >
          {row?.total_students ?? 0}
        </span>
      ),
    },
    {
      title: 'school.columns.status',
      key: 'status',
      width: '120px',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: { status: ESchoolStatus }) => {
        const config =
          SchoolStatusConfig[row?.status as keyof typeof SchoolStatusConfig];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'school.columns.subscription-expiry-date',
      key: 'subscription_expiry_date',
      width: '220px',
      sorter: true,
      handleSort: () => handleSort('subscription_expiry_date'),
      render: (row: ISchoolData) =>
        row?.subscription_expiry_date
          ? formatDate(row.subscription_expiry_date)
          : '-',
    },
    {
      title: 'school.columns.subscription-status',
      key: 'subscription_status',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('subscription_status'),
      render: (row: ISchoolData) => {
        if (!row?.subscription_status) return '-';
        const config = SubscriptionStatusConfig[row.subscription_status];
        if (!config) return '-';
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-medium',
              config.className
            )}
          >
            {formatText(config.label)}
          </span>
        );
      },
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '100px',
      align: 'center',
      render: (row: ISelectedRowValue) => {
        const isBlocked = row.status === ESchoolStatus.IN_ACTIVE;
        const school = {
          name: row.name,
          id: row.id,
          status: row.status,
        };

        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedSchool({ name: school.name, id: school.id });
              setIsOpenDetailsModal(true);
            },
          },
          {
            key: isBlocked ? 'unblock' : 'block',
            label: isBlocked
              ? 'common.actions.unblock'
              : 'common.actions.block',
            icon: isBlocked ? (
              <IconLockOpen size={18} />
            ) : (
              <IconXboxX size={18} />
            ),
            onClick: () => {
              if (isBlocked) {
                handleBlockSchool(school);
              } else {
                setSelectedSchool(school);
                setIsOpenBlockModal(true);
              }
            },
          },
          {
            key: 'remove',
            label: 'common.actions.remove',
            icon: <IconTrash size={18} />,
            isDanger: true,
            onClick: () => {
              setSelectedSchool({ name: school.name, id: school.id });
              setIsRemoveSchoolModalOpen(true);
            },
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const handleBlockSchool = async (school = selectedSchool) => {
    if (!school) return;

    setLoadingModal(true);

    try {
      const params = {
        id: school.id,
        record_type: ERestoreEntityType.SCHOOL,
        is_block: school.status === ESchoolStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        fetchSchools();
        fetchSummary();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
      setIsOpenBlockModal(false);
      setSelectedSchool(null);
    }
  };

  const handleRemoveSchool = async () => {
    if (!selectedSchool?.id) return;
    setLoadingModal(true);

    try {
      const params = {
        id: selectedSchool.id.toString(),
        record_type: ERestoreEntityType.SCHOOL,
      };
      const response = await softdeleteRecord(params);
      if (response?.status) {
        fetchSchools();
        fetchSummary();
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
      setIsRemoveSchoolModalOpen(false);
      setSelectedSchool(null);
    }
  };

  const isFilterApplied = subscriptionStatus !== '';

  const showListUI =
    loadingTable ||
    schools.length > 0 ||
    search.length > 0 ||
    debouncedSearch.length > 0 ||
    isFilterApplied;

  return (
    <>
      {showListUI && (
        <>
          <div className="flex flex-wrap justify-between items-center gap-2.5 mb-6 pl-1">
            <div className="flex items-center gap-2.5">
              <Input
                name="search"
                placeholder={formatText('school.search-placeholder')}
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearch(e.target.value)
                }
                leftIcon={{
                  icon: <IconSearch size={20} className="text-neutral-500" />,
                }}
                className="max-w-77 md:min-w-77 w-full"
                inputClassName="py-[13px]"
              />
              <Select
                placeholder={formatText(
                  'school.subscription-status-placeholder'
                )}
                options={SUBSCRIPTION_STATUS_OPTIONS}
                selected={subscriptionStatus}
                onChange={(value) =>
                  setSubscriptionStatus(value as ESubscriptionStatus | '')
                }
                className="w-37.5! py-3.25"
                isTextFormat
                isClearable
              />
            </div>
            <Button
              icon={<IconSchool size={20} />}
              label={formatText('school.actions.add-school')}
              variant="primary"
              className="px-4 h-12 cursor-pointer"
              onClick={() => setIsAddSchoolModalOpen(true)}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            {statsData.map((stat, index) => (
              <SummaryCard
                key={index}
                variant="dashboard"
                label={formatText(stat.label)}
                value={stat?.value}
                icon={stat?.icon}
                trendDirection={stat?.trendDirection}
                isPositive={stat?.isPositive}
                trendLabel={
                  stat?.trendLabel ? formatText(stat.trendLabel) : undefined
                }
                trend={stat?.trend}
                isLoading={loadingSummary}
              />
            ))}
          </div>
        </>
      )}
      <div className="mt-6">
        <CustomTable
          isLoading={loadingTable}
          dataSource={schools}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          fieldName={sortField}
          sortDirection={sortDirection}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          isEmpty={
            !loadingTable &&
            schools?.length === 0 &&
            search?.length === 0 &&
            debouncedSearch?.length === 0 &&
            !isFilterApplied
          }
          emptyTitle={formatText('user-management.school.not-found-title')}
          emptyDescription={formatText('user-management.school.not-found-desc')}
          emptyButtonText={formatText('user-management.school.add-school-btn')}
          emptyButtonIcon={<IconSchool size={20} />}
          onEmptyBtnClick={() => setIsAddSchoolModalOpen(true)}
          isEmptySearch={
            schools?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          onRowClick={(id) => {
            const school = schools.find((item) => item.id === id);
            if (school) {
              setSelectedSchool({ name: school.name, id: school.id });
              setIsOpenDetailsModal(true);
            }
          }}
        />
      </div>

      {isOpenDetailsModal && (
        <SchoolDetailsModal
          setIsOpen={setIsOpenDetailsModal}
          selectedSchool={selectedSchool}
          onSubscriptionUpdate={fetchSchools}
          onRemoveSubmit={() => {
            fetchSchools();
            fetchSummary();
            setIsOpenDetailsModal(false);
            setSelectedSchool(null);
          }}
        />
      )}
      {isOpenBlockModal && (
        <ConfirmationModal
          isOpen={isOpenBlockModal}
          setIsOpen={setIsOpenBlockModal}
          title="school.actions.block-school-title"
          descriptionPrefix="school.actions.block-school-prefix"
          descriptionSuffix="school.actions.block-school-suffix"
          entityName={selectedSchool?.name}
          confirmLabel={formatText('school.actions.block-school')}
          onConfirm={handleBlockSchool}
          isLoading={loadingModal}
          isDestructive
        />
      )}
      {isRemoveSchoolModalOpen && (
        <ConfirmationModal
          isOpen={isRemoveSchoolModalOpen}
          setIsOpen={setIsRemoveSchoolModalOpen}
          title="school.actions.remove-school-title"
          descriptionPrefix="school.actions.remove-school-prefix"
          descriptionSuffix="school.actions.remove-school-suffix"
          entityName={selectedSchool?.name}
          confirmLabel={formatText('school.actions.remove-school')}
          onConfirm={handleRemoveSchool}
          isLoading={loadingModal}
          isDestructive
        />
      )}
      {isAddSchoolModalOpen && (
        <AddSchoolModal
          setIsOpen={setIsAddSchoolModalOpen}
          onSubmit={() => {
            fetchSchools();
            fetchSummary();
          }}
        />
      )}
    </>
  );
};

export default School;
